import { useTranslation } from 'react-i18next'
import Breadcrumb from '../../../../components/common/Breadcrumb'
import Text from '../../../../components/common/Text'
import { FONT_BLACK, FONT_LIGHT_GRAY, INPUT_ERROR } from '../../../../constant/colors'
import { useGetUser, useGetUserCoin } from '../../../../services/user/user-query'
import IconUser from '../../../../assets/images/icons-user.svg'
import Image from '../../../../components/common/Image'
import { useCallback, useEffect, useMemo } from 'react'
import {
  useGetAllSelfBenefits,
  useSelfBenefitsSend,
} from '../../../../services/enrollment/enrollment-query'
import Loading from '../../../../components/common/Loading'
import { useScreen } from '../../../../utils/responsive-helper'
import { numberWithCommas, useCanEditable, useRouter } from '../../../../utils/helper'
import {
  BenefitChoices,
  BenefitsCategoryWrapperList,
  SelfBenefitsSendParams,
} from '../../../../services/enrollment/enrollment-types'
import {
  Layout,
  BreadcrumbLayout,
  Header,
  UserName,
  RedeemButton,
  SummaryPoint,
  FooterContentLayout,
  Row,
  ContentSpace,
} from './shared'
import { notification, Space } from 'antd'
import ModalAcceptPrivacy from '../../ModalAcceptPrivacy'
import { useVisibility } from '../../../../utils/custom-hook'
import dayjs from 'dayjs'
import { reduxForm, InjectedFormProps, getFormValues } from 'redux-form'
import { SubGroupBenefitsField } from './SubGroupBenefits'
import { concat } from 'lodash'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { benefits } from '../../../../constant/path'
import { useGetEnrollmentYear } from '../..'

export type SelectedChoices = Partial<BenefitChoices> &
  Pick<BenefitsCategoryWrapperList, 'benefitsCategoryId' | 'benefitsCategoryName'>

type EditSelfBenefitsFormValues = {
  [key: string]: SelectedChoices[]
}
const EDIT_SELF_BENEFITS_FORM_NAME = 'EditSelfBenefits'
const enchancer = reduxForm({ form: EDIT_SELF_BENEFITS_FORM_NAME })

type EditSelfBenefitsProps = InjectedFormProps<EditSelfBenefitsFormValues>
const EditSelfBenefits = (props: EditSelfBenefitsProps) => {
  const { handleSubmit, initialize, dirty } = props
  const { t } = useTranslation()
  const { query } = useRouter()
  const { isMobile } = useScreen()
  const [visible, modalAction] = useVisibility()
  const { enrollmentYear } = useGetEnrollmentYear()

  const { data: user, isLoading: isUserLoading } = useGetUser()
  const { data: userPoint = 0, isStale: isUserCoinStale } = useGetUserCoin()
  const { data: allSelfBenefits, isStale: isAllSelfBenefitsStale } = useGetAllSelfBenefits(
    query?.benefitYearId,
  )
  const { mutate: selfBenefitsSend, isLoading: isSubmitting } = useSelfBenefitsSend()
  const canEditable = useCanEditable()
  const history = useHistory()

  const formValuesSelector = getFormValues(EDIT_SELF_BENEFITS_FORM_NAME)
  const formValues = useSelector<any, EditSelfBenefitsFormValues>((state) =>
    formValuesSelector(state),
  )

  useEffect(() => {
    const initailValues = allSelfBenefits
      ? allSelfBenefits.reduce((initialValues: EditSelfBenefitsFormValues, cur) => {
        const selected = cur.benefitsCategoryWrapperList.reduce((acc: SelectedChoices[], v) => {
          const choiceSelected = v.choices.find((k) => k.selected === true)
          if (choiceSelected)
            acc.push({
              benefitsCategoryId: v.benefitsCategoryId,
              benefitsCategoryName: v.benefitsCategoryName,
              ...choiceSelected,
            })
          return acc
        }, [])
        initialValues[cur.benefitName] = selected

        return initialValues
      }, {})
      : {}
    initialize(initailValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSelfBenefits])

  const initialPoint = useMemo(() => {
    return allSelfBenefits
      ? allSelfBenefits.reduce((sumTotalPoint, cur) => {
        cur.benefitsCategoryWrapperList.forEach((v) => {
          const choiceSelected = v.choices.find((k) => k.selected === true)
          const point = choiceSelected?.taggedPrice || choiceSelected?.defaultCoinValue || 0
          sumTotalPoint += point
        })

        return sumTotalPoint
      }, 0)
      : 0
  }, [allSelfBenefits])

  const totalSelectedPoint = useMemo(() => {
    return formValues
      ? Object.entries(formValues).reduce((sumTotalPoint, cur) => {
        const [, value] = cur
        value.forEach((choiceSelected) => {
          const point = choiceSelected?.taggedPrice || choiceSelected?.defaultCoinValue || 0
          sumTotalPoint += point
        })
        return sumTotalPoint
      }, 0)
      : 0
  }, [formValues])

  const remainPoint = useMemo(() => {
    return userPoint + initialPoint - totalSelectedPoint
  }, [initialPoint, totalSelectedPoint, userPoint])

  const userAge = useMemo(() => {
    return user?.age ? user?.age : '-'
  }, [user])

  const isApiFetched = useMemo(() => {
    return !isUserCoinStale && !isAllSelfBenefitsStale && !isSubmitting
  }, [isAllSelfBenefitsStale, isSubmitting, isUserCoinStale])
  const canSubmit = useMemo(() => {
    return canEditable && allSelfBenefits && remainPoint > 0 && dirty
  }, [allSelfBenefits, canEditable, dirty, remainPoint])

  const onConfirmSelectedBenefits = useCallback(
    (formValues: EditSelfBenefitsFormValues) => {
      const selectedChoices = Object.entries(formValues).reduce(
        (acc: SelfBenefitsSendParams['selectedChoices'], [, value]) => {
          const temp = value.map((v) => ({
            selfBenefitsCategoryId: v.benefitsCategoryId,
            selfBenefitsChoicesId: v.id!,
          }))
          acc = concat(acc, temp)
          return acc
        },
        [],
      )
      const params: SelfBenefitsSendParams = {
        benefitYearId: query?.benefitYearId!,
        selectedChoices: selectedChoices,
      }

      if (selectedChoices.length) {
        selfBenefitsSend(params, {
          onSuccess: () => {
            notification.success({
              message: 'Success',
            })
            history.push(
              benefits({
                routeParam: {
                  benefitYearId: query.benefitYearId,
                },
              }),
            )
          },
          onError: () => {
            notification.error({
              message: 'Fail',
            })
          },
        })
      }
      modalAction.hide()
    },
    [query?.benefitYearId, modalAction, selfBenefitsSend, history],
  )

  return (
    <Layout>
      <ModalAcceptPrivacy
        visible={visible}
        onConfirmClick={handleSubmit(onConfirmSelectedBenefits)}
        onCloseClick={modalAction.hide}
      />
      {!isMobile && (
        <BreadcrumbLayout>
          <Breadcrumb
            overide={(paths) => {
              // paths.unshift({ label: 'Home', path: '/' })
              paths[0].label = t('ปรับเปลี่ยนสวัสดิการ')
              paths[1].label = t(`ดูสวัสดิการปี ${enrollmentYear}`)
              paths[2].label = t(`สวัสดิการปี ${enrollmentYear} ของฉัน`)
              paths[3].label = t('enrollment.myInsurance')
              return paths
            }}
          />
        </BreadcrumbLayout>
      )}
      <Header>
        <Text size={28} type="Bold" color={FONT_BLACK}>
          ประกันของตัวเอง
        </Text>
      </Header>
      <UserName>
        <div>
          <Image src={IconUser} />
        </div>
        <Loading loading={isUserLoading}>
          <Row>
            <Space className="username" direction="horizontal" size={8}>
              <Text size={26} type="Bold" color={FONT_BLACK}>
                {user?.firstName}
              </Text>
              <Text size={26} type="Bold" color={FONT_BLACK}>
                {user?.lastName}
              </Text>
            </Space>
            <Text size={26} type="Bold" color={FONT_BLACK}>{`(${userAge} ปี)`}</Text>
          </Row>
        </Loading>
      </UserName>
      <ContentSpace direction="vertical" size={10}>
        {allSelfBenefits?.map((benefit, index) => {
          return (
            <SubGroupBenefitsField
              key={`${benefit.benefitName}-${index}`}
              name={benefit.benefitName}
              benefit={benefit}
            />
          )
        })}
      </ContentSpace>
      <FooterContentLayout
        style={{
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-end',
          alignItems: 'center',
          padding: '16px 0',
        }}
      >
        <div>
          <Text size={22} type="Bold" color={FONT_BLACK}>
            รวมใช้คะแนน
          </Text>
          <SummaryPoint>
            <Text size={22} type="Bold" color={INPUT_ERROR}>
              {numberWithCommas(-totalSelectedPoint)}
            </Text>
            <Text size={20} color={FONT_BLACK}>
              คะแนน
            </Text>
            <Text size={20} color={FONT_LIGHT_GRAY}>
              {`(คงเหลือ ${numberWithCommas(remainPoint)} คะแนน)`}
            </Text>
          </SummaryPoint>
        </div>
        <RedeemButton onClick={modalAction.show} disabled={!canSubmit || !isApiFetched}>
          {t('เลือก')}
        </RedeemButton>
      </FooterContentLayout>
    </Layout>
  )
}
export default enchancer(EditSelfBenefits)
